import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select } from '@mantine/core';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import { getConsumptionsSummary } from '../../../../../slices/thunks';
import OChartCard from '../../../../../Components/OElements/OChartCard';
import OBarChart from '../../../../../Components/OElements/OBarChart';

const ConsumptionsManagementEChart = ({
	title,
	legendPosition = 'bottom',
	defaultFilters = {
		option: 'dia',
	},
	minHeight,
	maxHeight,
	companyId = null,
}) => {
	const dispatch = useDispatch();
	const [selectedDate, setSelectedDate] = useState(defaultFilters.option);
	const [selectedFuelType, setSelectedFuelType] = useState('diesel');

	const selectorConsumptions = createSelector(
		state => state.FuelSupply,
		data => ({ info: data.consumptionsSummaryManagement || null })
	);
	const { info: infoConsumptions } = useSelector(selectorConsumptions);

	const fetchConsumptionsData = useCallback(
		(filter, company_id) => {
			dispatch(
				getConsumptionsSummary({
					// NO establishment_id here - this is for management view
					filter: filter,
					group: 'establecimiento', // Group by establishment
					company_id: company_id, // Add company filter
				})
			);
		},
		[dispatch]
	);

	useEffect(() => {
		fetchConsumptionsData(selectedDate, companyId);
	}, [selectedDate, companyId, fetchConsumptionsData]);

	// Extract unique fuel types from API response
	const availableFuelTypes = useMemo(() => {
		if (!infoConsumptions?.data || !Array.isArray(infoConsumptions.data))
			return [];

		const fuelTypesSet = new Set();
		infoConsumptions.data.forEach(item => {
			if (item.establishments && Array.isArray(item.establishments)) {
				item.establishments.forEach(est => {
					if (est.fuel_types && Array.isArray(est.fuel_types)) {
						est.fuel_types.forEach(fuel => {
							if (fuel.name) {
								fuelTypesSet.add(fuel.name.toLowerCase());
							}
						});
					}
				});
			}
		});

		return Array.from(fuelTypesSet);
	}, [infoConsumptions?.data]);

	// Set default fuel type to 'diesel' when available fuel types change
	useEffect(() => {
		if (availableFuelTypes.length > 0) {
			// Check if current selected fuel type is available
			if (!availableFuelTypes.includes(selectedFuelType.toLowerCase())) {
				// Try to set diesel as default, otherwise use first available
				if (availableFuelTypes.includes('diesel')) {
					setSelectedFuelType('diesel');
				} else {
					setSelectedFuelType(availableFuelTypes[0]);
				}
			}
		}
	}, [availableFuelTypes]);

	const { series, categories, legendData } = useMemo(() => {
		if (!infoConsumptions?.data || !Array.isArray(infoConsumptions.data))
			return { series: [], categories: [], legendData: [] };

		// Extract unique dates
		const dates = [...new Set(infoConsumptions.data.map(item => item.date))];

		// Extract unique establishments
		const establishmentsMap = new Map();
		infoConsumptions.data.forEach(item => {
			if (item.establishments && Array.isArray(item.establishments)) {
				item.establishments.forEach(est => {
					if (!establishmentsMap.has(est.establishment_id)) {
						establishmentsMap.set(
							est.establishment_id,
							est.establishment_name
						);
					}
				});
			}
		});

		const establishments = Array.from(establishmentsMap.entries());

		// Create series for each establishment, filtered by selected fuel type
		const seriesData = establishments.map(([estId, estName]) => ({
			name: estName,
			type: 'bar',
			stack: 'total',
			data: dates.map(date => {
				const dateData = infoConsumptions.data.find(
					item => item.date === date
				);
				if (!dateData || !dateData.establishments) return 0;

				const estData = dateData.establishments.find(
					e => e.establishment_id === estId
				);

				if (!estData || !estData.fuel_types) return 0;

				// Find the selected fuel type and return its consumption
				const fuelTypeData = estData.fuel_types.find(
					fuel =>
						fuel.name &&
						fuel.name.toLowerCase() === selectedFuelType.toLowerCase()
				);

				return fuelTypeData ? Number(fuelTypeData.consumption) || 0 : 0;
			}),
		}));

		return {
			series: seriesData,
			categories: dates,
			legendData: establishments.map(([_, name]) => name),
		};
	}, [infoConsumptions?.data, selectedFuelType]);

	const options = useMemo(
		() => ({
			tooltip: {
				trigger: 'axis',
				axisPointer: { type: 'shadow' },
				formatter: function (params) {
					if (!params || !params.length) return '';
					let result = params[0].axisValue + '<br/>';
					let total = 0;

					params.forEach(param => {
						if (param.value > 0) {
							result +=
								param.marker +
								' ' +
								param.seriesName +
								': ' +
								param.value.toFixed(2) +
								' L<br/>';
							total += param.value;
						}
					});

					result += '<b>Total: ' + total.toFixed(2) + ' Litros</b>';
					return result;
				},
			},
			legend: {
				data: legendData,
				orient: 'horizontal',
				bottom: 10,
				type: 'scroll',
			},
			xAxis: [{ type: 'category', data: categories }],
			yAxis: [{ type: 'value', name: 'Litros' }],
			series: series,
		}),
		[series, categories, legendData]
	);

	const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
	const [hasValidData, setHasValidData] = useState(false);

	useEffect(() => {
		if (Array.isArray(infoConsumptions?.data)) {
			setHasLoadedOnce(true);
			if (infoConsumptions.data.length > 0) {
				setHasValidData(true);
			}
		}
	}, [infoConsumptions?.data]);

	// Only show empty state if we've loaded and confirmed there's no data
	const noData = hasLoadedOnce && !infoConsumptions?.data?.length;

	const actions = (
		<div className='btn-box-dashboard' style={{ display: 'flex', gap: 8 }}>
			<Select
				placeholder='Tipo de Combustible'
				data={availableFuelTypes.map(type => ({
					label: type.charAt(0).toUpperCase() + type.slice(1),
					value: type,
				}))}
				value={selectedFuelType}
				onChange={value => value && setSelectedFuelType(value)}
				size='xs'
				radius='lg'
				styles={{ input: { width: 150 } }}
				disabled={availableFuelTypes.length === 0}
			/>
			<Select
				placeholder='Fecha'
				data={[
					{ label: 'Ciclo', value: 'ciclo' },
					{ label: 'Año', value: 'año' },
					{ label: 'Semestre', value: 'semestre' },
					{ label: 'Mes', value: 'mes' },
					{ label: 'Día', value: 'dia' },
				]}
				value={selectedDate}
				onChange={value => value && setSelectedDate(value)}
				size='xs'
				radius='lg'
				styles={{ input: { width: 100 } }}
			/>
		</div>
	);

	return (
		<OChartCard
			title={title}
			actions={actions}
			isLoading={!hasLoadedOnce && !infoConsumptions?.data}
			error={null}
			isEmpty={noData}
			emptyIconClass='ri-gas-station-fill'
			emptyMessage='No cuenta con registro de consumo de combustible'
		>
			<OBarChart
				options={options}
				minHeight={minHeight}
				maxHeight={maxHeight}
			/>
		</OChartCard>
	);
};

export default React.memo(ConsumptionsManagementEChart);
